import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { filter, find, first, isArray, isNil, isString } from 'lodash'

import { Countries } from '../../data'
import { Clinician } from '../../model/clinician'
import { IQuestionOptions } from '../../model/question'
import { CustomQuestionSchema } from '../../schema'
import { Address, SelectOption } from '../'
import { PatientIntakeFormData } from '../intake-form-data.class'
import { QuestionBase, QuestionOptions } from './base.question.class'

interface AddressQuestionOptions extends QuestionOptions<Address> {
  options?: SelectOption<string>[] | IQuestionOptions // countries
  states?: SelectOption<string>[]
}

export class AddressQuestion extends QuestionBase<Address> {
  controlType = 'address'
  flex = '100%'
  declare options: SelectOption<string>[]
  states: SelectOption<string>[]
  multiple = false

  constructor(options: AddressQuestionOptions = {}) {
    super(options)
    if (this.isCustom) {
      this.options = Countries
    } else if (isArray(options.options)) {
      this.options = options.options
    } else {
      this.options = []
    }
    this.states = options.states || []
  }

  toFormControls(fb: UntypedFormBuilder): { [key: string]: UntypedFormGroup } {
    const addressControl: { [key: string]: UntypedFormControl } = {}

    if (isNil(this.value)) {
      this.value = new Address()
    }

    const fields = [
      'address1',
      'address2',
      'address3',
      'sublocality',
      'city',
      'state',
      'zip',
      'county',
      'country',
    ]

    for (const field of fields) {
      let validators = []

      // address 2 and 3 are not required
      if (
        field !== 'address2' &&
        field !== 'address3' &&
        field !== 'sublocality'
      ) {
        validators = this.getValidators()
      }
      if (field === 'city') {
        validators.push(Validators.pattern(/^[^0-9]*$/))
      }

      addressControl[field] = fb.control(this.value[field], validators)
    }

    // make special controls that lets us support autofill
    if (this.autocomplete) {
      // this.controls.countryString = fb.control(this.value.country)
      // this.controls.countryString.valueChanges.subscribe((val) => {
      //   if (val) {
      //     this.controls.country.setValue(val)
      //   }
      // })

      addressControl.stateString = fb.control(this.value.state)
      addressControl.stateString.valueChanges.subscribe((val) => {
        if (addressControl.country.value === 'US' && isString(val)) {
          const option = find(this.states, (state: SelectOption<string>) => {
            return (
              state.value === val.toUpperCase() ||
              state.viewValue.toLowerCase() === val.toLowerCase()
            )
          })

          if (option) {
            addressControl.state.setValue(option.value)
          }
        }
      })
    }
    if (this.isCustom) {
      this.control = fb.group({
        value: fb.group(addressControl),
        note: fb.control(this.note),
      })
      return {
        [this.id]: this.control,
      }
    } else {
      this.control = fb.group({
        value: fb.group(addressControl),
      })
      return {
        [this.key]: this.control,
      }
    }
  }

  valueFromData(data: PatientIntakeFormData) {
    if (this.isCustom) {
      const value = first(filter(data.customQuestions, { id: this.id }))
      if (value) {
        this.value = value.value as any
        this.note = value.note
        this.clinicianId = value.clinicianId
      } else {
        this.value = new Address()
      }
    } else {
      this.value = data[this.key]
    }
  }

  saveValueToData(
    data: PatientIntakeFormData,
    form: UntypedFormGroup,
    clinician: Clinician,
  ) {
    let value: Address
    let note: string

    if (this.isCustom) {
      value = form.value[this.id].value
      note = form.value[this.id].note
    } else {
      value = form.value[this.key].value
    }

    if (isNil(value.address1) && isNil(value.city)) {
      value = null
    }

    if (this.isCustom) {
      const customData: CustomQuestionSchema = {
        id: this.id,
        value,
        note,
      }

      data.saveCustomQuestion(customData, clinician.id)
    } else {
      data[this.key] = value
    }
  }
}
